import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaPaperclip, FaFile, FaReply, FaTimes, FaHistory, FaCheck, FaCheckDouble } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatChatDate, formatChatTime, shouldShowDateSeparator } from "../../utils/dateUtils";
import {
  CHAT_HISTORY_LIMIT,
  appendSelectedFiles,
  emitChatTyping,
  emitFileUploading,
  mergeUniqueMessages,
  normalizeMessageFiles,
  parseHistoryResponse,
  truncateFileName,
  getSupportAgentName,
  formatSupportHeaderTitle,
} from "../../utils/chatHelpers";
import ChatFileBubble from "../../components/chat/ChatFileBubble";
import io from "socket.io-client";
import { useTheme } from "../../context/SocketContext";
import ChatHistory from "../../components/ChatHistory";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const UserChatbox = ({ user, onClose }) => {
  const { currentThemeColors } = useTheme();
  const primaryGradient = currentThemeColors?.primary
    ? `linear-gradient(135deg, ${currentThemeColors.primary}, ${currentThemeColors.primaryDark || currentThemeColors.primary})`
    : 'linear-gradient(to right, #2563eb, #1d4ed8)';
  const userBubbleColor = currentThemeColors?.primary || '#2563eb';
  const accentColor = currentThemeColors?.accent || currentThemeColors?.secondary || '#f59e0b';
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [errorState, setErrorState] = useState("");
  const setError = (msg) => {
    setErrorState(msg);
    if (msg) {
      setTimeout(() => {
        setErrorState("");
      }, 5000);
    }
  };
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const error = errorState;
  const [adminTyping, setAdminTyping] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isAdminUploading, setIsAdminUploading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const textareaRef = useRef(null);
  const sendingLock = useRef(false);
  const [chatboxSize, setChatboxSize] = useState({ width: 380, height: 500 });
  const resizeRef = useRef({ isResizing: false, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [assignedEmployee, setAssignedEmployee] = useState(null);

  // Enhanced textarea handling for mobile
  const handleTextareaChange = (e) => {
    const textarea = e.target;

    // Clear previous height
    textarea.style.height = 'auto';

    // Calculate new height with constraints
    const newHeight = Math.min(textarea.scrollHeight, 120); // Same as maxHeight in style
    const minHeight = 20; // Minimum height for single line

    // Ensure minimum height for mobile usability
    textarea.style.height = Math.max(newHeight, minHeight) + 'px';

    setNewMessage(textarea.value);

    const emitTyping = () => {
      emitChatTyping(socketRef.current, {
        role: 'user',
        userId: user._id,
        isTyping: textarea.value.length > 0,
      });
    };

    if (!isMobile) {
      emitTyping();
    } else {
      clearTimeout(window.typingTimeout);
      window.typingTimeout = setTimeout(emitTyping, 500);
    }
  };

  // Mark messages as read when chatbox is open
  const markMessagesAsRead = useCallback(async () => {
    if (!user) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, userType: 'user' }),
      });
    } catch (err) { console.error('Mark as read error:', err); }
  }, [user]);

  // Mark messages as delivered when user opens chatbox
  const markMessagesAsDelivered = useCallback(async () => {
    if (!user) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-delivered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, userType: 'user' }),
      });
    } catch (err) { console.error('Mark as delivered error:', err); }
  }, [user]);

  const fetchAssignedEmployee = useCallback(async () => {
    if (!user?._id) return;
    try {
      const response = await fetch(`${API_BASE}/api/chat/assigned-employee/${user._id}`);
      if (!response.ok) throw new Error('Failed to fetch assigned employee.');
      const data = await response.json();
      setAssignedEmployee(data.employee || null);
    } catch (err) {
      console.error('Assigned employee fetch error:', err);
    }
  }, [user?._id]);

  useEffect(() => {
    fetchAssignedEmployee();
  }, [fetchAssignedEmployee]);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!user) return;

    socketRef.current = io(process.env.REACT_APP_API_BASE_URL, {
      query: { userId: user._id }
    });

    socketRef.current.emit('joinRoom', user._id);
    socketRef.current.emit('userChatOpen', { userId: user._id, isOpen: true });

    socketRef.current.on('newMessage', (message) => {
      setMessages(prevMessages => [...prevMessages, message]);
      // Auto-mark as read since user has chat open
      markMessagesAsRead();
    });

    socketRef.current.on('adminTyping', ({ isTyping }) => {
      setAdminTyping(isTyping);
    });

    // Listen for read receipts from admin
    socketRef.current.on('messagesRead', ({ userId, readAt, readBy }) => {
      if (readBy === 'admin') {
        setMessages(prev => prev.map(msg =>
          msg.sender === 'user' && !msg.readAt
            ? { ...msg, isRead: true, readAt }
            : msg
        ));
      }
    });

    // Listen for delivery receipts
    socketRef.current.on('messagesDelivered', ({ deliveredAt }) => {
      setMessages(prev => prev.map(msg =>
        msg.sender === 'user' && !msg.deliveredAt
          ? { ...msg, deliveredAt }
          : msg
      ));
    });

    // Listen for message edits
    socketRef.current.on('messageEdited', ({ messageId, newMessage, isEdited, editedAt }) => {
      setMessages(prev => prev.map(msg =>
        msg._id === messageId
          ? { ...msg, message: newMessage, isEdited, editedAt }
          : msg
      ));
    });

    // Mark existing messages as read & delivered on open
    markMessagesAsRead();
    markMessagesAsDelivered();

    socketRef.current.on('fileUploading', ({ isUploading, fromAdmin }) => {
      if (fromAdmin) {
        setIsAdminUploading(isUploading);
      }
    });

    socketRef.current.on('assignedEmployeeUpdated', ({ employee }) => {
      setAssignedEmployee(employee || null);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('userChatOpen', { userId: user._id, isOpen: false });
        socketRef.current.disconnect();
      }
    };
  }, [user, markMessagesAsRead, markMessagesAsDelivered]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      // 1024px covers iPad Air and other larger tablets
      const mobileCheck = window.innerWidth <= 1024;
      setIsMobile(mobileCheck);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced mobile keyboard and input handling
  useEffect(() => {
    if (!isMobile) return;

    // Set CSS custom property for proper viewport height calculation
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial value
    setVH();

    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => {
      // Small delay to ensure viewport is updated after orientation change
      setTimeout(setVH, 100);
    });

    let keyboardVisible = false;

    // Prevent zoom on input focus for iOS
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      const originalContent = viewport.getAttribute('content');

      const handleFocus = () => {
        // Set maximum scale to 1.0 to prevent zooming
        viewport.setAttribute('content', originalContent + ', maximum-scale=1.0');
        keyboardVisible = true;

        // Add keyboard-visible class for CSS adjustments
        const footer = document.querySelector('.user-chatbox-footer');
        if (footer) {
          footer.classList.add('keyboard-visible');
        }

        const inputContainer = document.querySelector('.user-chatbox-input-container');
        if (inputContainer) {
          inputContainer.classList.add('keyboard-visible');
        }

        // Scroll to bottom to ensure input is visible
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      };

      const handleBlur = () => {
        // Restore original viewport settings
        viewport.setAttribute('content', originalContent);
        keyboardVisible = false;

        // Remove keyboard-visible class
        const footer = document.querySelector('.user-chatbox-footer');
        if (footer) {
          footer.classList.remove('keyboard-visible');
        }

        const inputContainer = document.querySelector('.user-chatbox-input-container');
        if (inputContainer) {
          inputContainer.classList.remove('keyboard-visible');
        }
      };

      const handleVisualViewportChange = () => {
        if (window.visualViewport) {
          const viewport = window.visualViewport;
          const heightDiff = window.innerHeight - viewport.height;

          if (heightDiff > 150) { // Keyboard is likely visible
            if (!keyboardVisible) {
              handleFocus();
            }
          } else { // Keyboard is likely hidden
            if (keyboardVisible) {
              handleBlur();
            }
          }
        }
      };

      const textarea = textareaRef.current;
      if (textarea) {
        textarea.addEventListener('focus', handleFocus);
        textarea.addEventListener('blur', handleBlur);

        // Handle visual viewport changes for better keyboard detection
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', handleVisualViewportChange);
        }

        return () => {
          textarea.removeEventListener('focus', handleFocus);
          textarea.removeEventListener('blur', handleBlur);
          if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
          }
        };
      }
    }

    // Handle orientation changes
    const handleOrientationChange = () => {
      setTimeout(() => {
        // Adjust chatbox position after orientation change
        const chatbox = document.querySelector('.user-chatbox');
        if (chatbox) {
          chatbox.style.height = window.innerHeight + 'px';
          chatbox.style.width = window.innerWidth + 'px';

          // Update safe area insets dynamically
          updateSafeAreaInsets();
          // Update viewport height
          setVH();

          // Force footer to be visible
          const footer = chatbox.querySelector('.user-chatbox-footer');
          if (footer) {
            footer.style.position = 'relative';
            footer.style.zIndex = '1000';
            footer.style.width = '100%';
          }
        }
      }, 100);
    };

    // Function to update safe area insets dynamically
    const updateSafeAreaInsets = () => {
      const chatbox = document.querySelector('.user-chatbox');
      if (chatbox && window.getComputedStyle) {
        const computedStyle = window.getComputedStyle(document.documentElement);
        const bottomInset = computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || '0px';
        const leftInset = computedStyle.getPropertyValue('env(safe-area-inset-left)') || '0px';
        const rightInset = computedStyle.getPropertyValue('env(safe-area-inset-right)') || '0px';

        chatbox.style.paddingBottom = `calc(${bottomInset} + 0px)`;
      }
    };

    // Update safe area insets on load and resize
    updateSafeAreaInsets();
    window.addEventListener('resize', updateSafeAreaInsets);

    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', updateSafeAreaInsets);
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, [isMobile]);

  // Auto-scroll when near bottom
  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchChatHistory = useCallback(async (before) => {
    if (!user?._id) return { batch: [], hasMore: false };
    const params = new URLSearchParams({
      userId: user._id,
      limit: String(CHAT_HISTORY_LIMIT),
    });
    if (before) params.set('before', before);

    const response = await fetch(`${API_BASE}/api/chat/history?${params}`);
    if (!response.ok) throw new Error('Failed to fetch chat history.');
    const data = await response.json();
    const { messages: batch, hasMore: more } = parseHistoryResponse(data);
    return { batch, hasMore: more };
  }, [user?._id]);

  useEffect(() => {
    if (!user) return;

    const loadInitial = async () => {
      try {
        const { batch, hasMore: more } = await fetchChatHistory();
        setMessages(batch);
        setHasMore(more);
        shouldAutoScrollRef.current = true;
      } catch (err) {
        setError(err.message);
      }
    };

    loadInitial();
  }, [user, fetchChatHistory]);

  const loadOlderMessages = async () => {
    if (!hasMore || loadingMore || messages.length === 0) return;
    setLoadingMore(true);
    shouldAutoScrollRef.current = false;
    const container = messagesContainerRef.current;
    const prevHeight = container?.scrollHeight || 0;

    try {
      const { batch, hasMore: more } = await fetchChatHistory(messages[0].timestamp);
      setMessages((prev) => mergeUniqueMessages(batch, prev));
      setHasMore(more);
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevHeight;
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMessagesScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    shouldAutoScrollRef.current = scrollHeight - scrollTop - clientHeight < 80;
    if (scrollTop < 40) {
      loadOlderMessages();
    }
  };

  const scrollToBottom = () => {
    shouldAutoScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    setNewMessage(""); // Clear current message when starting reply
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const scrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  const handleMessageSend = async () => {
    // ... existing code ...
    // Prevent multiple sends by checking lock
    if (sendingLock.current) return;

    if (newMessage.trim() !== "" || selectedFiles.length > 0) {
      // Set lock to prevent multiple sends
      sendingLock.current = true;
      setIsSending(true);
      setSendProgress(10); // Start progress

      const messageText = newMessage.trim();

      try {
        if (selectedFiles.length > 0) {
          setSendProgress(30);
          await uploadFiles();
          setSendProgress(70);
        } else {
          setSendProgress(50);
          await sendTextMessage(messageText);
        }
        setSendProgress(100);

        setTimeout(() => {
          setIsSending(false);
          setSendProgress(0);
        }, 500);
      } catch (error) {
        setError("Failed to send message. Please try again.");
        setIsSending(false);
        setSendProgress(0);
      } finally {
        // Release lock after sending completes or fails
        sendingLock.current = false;
      }
    }
  };

  const sendTextMessage = async (messageText) => {
    if (!messageText) return;

    const tempId = `temp-${Date.now()}`;
    const replyTarget = replyingTo;
    const instantMessage = {
      _id: tempId,
      user: user ? user.name : 'Guest',
      message: messageText,
      sender: 'user',
      timestamp: new Date().toISOString(),
      userId: user._id,
      pending: true,
      replyTo: replyTarget || null,
    };

    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, instantMessage]);
    setNewMessage('');
    setReplyingTo(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const newMessageObj = {
      user: user ? user.name : 'Guest',
      message: messageText,
      sender: 'user',
      timestamp: new Date().toISOString(),
      userId: user._id,
      replyTo: replyTarget ? replyTarget._id : null,
    };

    try {
      const response = await fetch(`${API_BASE}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(newMessageObj),
      });

      if (!response.ok) throw new Error('Failed to send message.');

      const data = await response.json();
      setMessages((prev) => prev.map((msg) => (msg._id === tempId ? { ...data, pending: false } : msg)));
      emitChatTyping(socketRef.current, { role: 'user', userId: user._id, isTyping: false });
    } catch (err) {
      setMessages((prev) => prev.map((msg) => (
        msg._id === tempId ? { ...msg, failed: true, pending: false } : msg
      )));
      setError('Failed to send message to server. Please check your connection.');
    }
  };

  const processFiles = (files) => {
    const { files: nextFiles, error: fileError } = appendSelectedFiles(selectedFiles, files);
    if (fileError) {
      setError(fileError);
      return;
    }
    setSelectedFiles(nextFiles);
    setError('');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    // Clear the input value to allow re-selecting the same file
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isMobile) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!isMobile) {
      const files = Array.from(e.dataTransfer.files);
      processFiles(files);
    }
  };

  const removeSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem("token");
    const formData = new FormData();

    // Create unique timestamp for this batch of files
    const batchTimestamp = Date.now();

    // Add temporary file messages with pending status
    const tempMessages = selectedFiles.map((file, index) => ({
      _id: `temp-file-${batchTimestamp}-${index}`,
      user: user ? user.name : "Guest",
      message: `File uploaded: ${file.name}`,
      sender: "user",
      timestamp: new Date().toISOString(),
      originalName: file.name,
      userId: user._id,
      pending: true
    }));

    shouldAutoScrollRef.current = true;
    setMessages(prevMessages => [...prevMessages, ...tempMessages]);

    selectedFiles.forEach(file => {
      formData.append("files", file);
    });

    formData.append("user", user ? user.name : "Guest");
    formData.append("sender", "user");
    formData.append("userId", user._id);
    formData.append("replyTo", replyingTo ? replyingTo._id : "");
    const caption = newMessage.trim();
    if (caption) {
      formData.append("message", caption);
    }

    emitFileUploading(socketRef.current, { role: 'user', userId: user._id, isUploading: true });

    try {
      const response = await fetch(`${API_BASE}/api/chat/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload files.");
      }

      const data = await response.json();

      setMessages(prevMessages => {
        const filteredMessages = prevMessages.filter(msg => !msg._id.startsWith(`temp-file-${batchTimestamp}`));
        return [...filteredMessages, ...(Array.isArray(data) ? data : [data])];
      });

      setSelectedFiles([]);
      setNewMessage('');
      setReplyingTo(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg._id.startsWith(`temp-file-${batchTimestamp}`) ?
            { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    } finally {
      emitFileUploading(socketRef.current, { role: 'user', userId: user._id, isUploading: false });
    }
  };

  const formatTime = (timestamp) => formatChatTime(timestamp);

  // Get tick icon for read receipts (for user's own messages)
  const getTickIcon = (msg) => {
    if (msg.sender !== 'user') return null;
    if (msg.pending) return null;
    if (msg.failed) return null;

    if (msg.isRead || msg.readAt) {
      // Blue double tick = read
      return <FaCheckDouble className="w-3 h-3 text-blue-400" />;
    }
    if (msg.deliveredAt) {
      // Gray double tick = delivered
      return <FaCheckDouble className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.6)' }} />;
    }
    // Single gray tick = sent
    return <FaCheck className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.6)' }} />;
  };

  const handleMouseDown = (e) => {
    if (isMobile) return;

    resizeRef.current.isResizing = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startWidth = chatboxSize.width;
    resizeRef.current.startHeight = chatboxSize.height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!resizeRef.current.isResizing || isMobile) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    // Invert deltaX to make left drag increase width and right drag decrease width
    const newWidth = Math.max(300, Math.min(800, resizeRef.current.startWidth - deltaX));
    // Invert deltaY to make up drag increase height and down drag decrease height
    const newHeight = Math.max(400, Math.min(800, resizeRef.current.startHeight - deltaY));

    setChatboxSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    if (isMobile) return;

    resizeRef.current.isResizing = false;

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save size preference to localStorage
    localStorage.setItem('userChatboxSize', JSON.stringify(chatboxSize));
  };

  // Load saved size preference on component mount
  useEffect(() => {
    const savedSize = localStorage.getItem('userChatboxSize');
    if (savedSize) {
      try {
        const parsedSize = JSON.parse(savedSize);
        // Ensure constraints are respected
        const constrainedSize = {
          width: Math.max(300, Math.min(800, parsedSize.width || 380)),
          height: Math.max(400, Math.min(800, parsedSize.height || 500))
        };
        setChatboxSize(constrainedSize);
      } catch (error) {
        console.error('Error parsing saved user chatbox size:', error);
      }
    }
  }, []);

  // Safety check - don't render if no user
  if (!user) return null;

  const supportHeaderTitle = formatSupportHeaderTitle(assignedEmployee);
  const supportHeaderInitial = assignedEmployee?.name?.charAt(0)?.toUpperCase() || 'B';
  const assistingMessage = assignedEmployee?.name
    ? `${assignedEmployee.name} is assisting you.`
    : 'You are chatting with BooksComply Support.';

  return (
    <div
      className={`user-chatbox fixed flex flex-col bg-white dark:bg-dark-800 rounded-2xl shadow-2xl overflow-hidden ${isMobile
          ? 'inset-0 rounded-none z-[9999]'
          : 'bottom-4 right-4 z-[9999]'
        } ${isDragging ? 'ring-2 ring-primary-500 scale-[1.01]' : ''}`}
      style={isMobile ? {} : { 
        width: chatboxSize.width, 
        height: chatboxSize.height, 
        maxWidth: '94vw',
        maxHeight: '85vh',
        transition: 'all 0.2s ease' 
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-[10000] bg-primary-600/10 backdrop-blur-[2px] flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-dark-800 p-8 rounded-3xl shadow-2xl border-2 border-dashed border-primary-500 flex flex-col items-center gap-4 animate-bounce">
            <div className="w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <FaPaperclip className="w-8 h-8 text-primary-600" />
            </div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">Drop files to share</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 text-white shrink-0"
        style={{ background: primaryGradient }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold shrink-0 overflow-hidden">
            {assignedEmployee?.profileImage ? (
              <img src={assignedEmployee.profileImage} alt={assignedEmployee.name} className="w-full h-full object-cover" />
            ) : (
              supportHeaderInitial
            )}
          </div>
          <div className="min-w-0">
            <h5 className="font-semibold truncate">{supportHeaderTitle}</h5>
            <p className="text-xs text-white/70 truncate">{assistingMessage}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            title="View Chat History"
          >
            <FaHistory className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages Body */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3 bg-gray-50 dark:bg-dark-900"
      >
        {loadingMore && (
          <div className="text-center py-2 text-xs text-gray-500 dark:text-gray-400">Loading older messages...</div>
        )}

        {assignedEmployee?.name && (
          <div className="flex justify-center">
            <span className="px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 text-xs text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800">
              You are chatting with {assignedEmployee.name}
            </span>
          </div>
        )}

        {messages.map((msg, index) => {
          const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);
          const isUser = msg.sender === 'user';
          const files = normalizeMessageFiles(msg);
          const showText = msg.message && !(files.length && msg.message.startsWith('File uploaded:'));
          const agentName = getSupportAgentName(msg);

          return (
            <React.Fragment key={msg._id || `msg-${index}`}>
              {showDateSeparator && (
                <div className="flex items-center justify-center my-4">
                  <span className="px-3 py-1 rounded-full bg-gray-200 dark:bg-dark-700 text-xs text-gray-500 dark:text-gray-400">
                    {formatChatDate(msg.timestamp)}
                  </span>
                </div>
              )}
              <div id={`message-${msg._id}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${msg.pending ? 'opacity-70' : ''} ${msg.failed ? 'opacity-50' : ''}`} style={{ transition: 'background-color 0.3s ease' }}>
                <div className={`group relative max-w-[80%] ${isUser ? 'order-2' : 'order-1'}`}>
                  {/* Reply Button - Show on all messages */}
                  <button
                    onClick={() => handleReply(msg)}
                    className={`absolute top-1/2 -translate-y-1/2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-gray-100 dark:bg-dark-600 text-gray-500 hover:bg-gray-200 dark:hover:bg-dark-500 z-10 ${isUser ? '-left-8' : '-right-8'
                      }`}
                    title="Reply"
                  >
                    <FaReply className="w-3 h-3" />
                  </button>
                  <div
                    className={`p-3 rounded-2xl ${isUser
                        ? 'text-white rounded-br-md'
                        : 'bg-white dark:bg-dark-800 text-dark-800 dark:text-white rounded-bl-md shadow-sm'
                      } ${msg.replyTo ? 'pt-2' : ''}`}
                    style={isUser ? { backgroundColor: userBubbleColor } : {}}
                  >
                    {!isUser && (
                      <p className="text-xs font-semibold text-primary-600 dark:text-primary-400 mb-1.5">
                        {assignedEmployee?.name || agentName || 'Support Team'}
                      </p>
                    )}

                    {/* Reply Preview */}
                    {msg.replyTo && (
                      <div
                        onClick={() => scrollToMessage(msg.replyTo._id)}
                        className={`mb-2 p-2 rounded-lg text-xs border-l-2 cursor-pointer hover:opacity-80 transition-opacity ${isUser ? 'bg-white/20 border-white/40' : 'bg-gray-100 dark:bg-dark-700 border-gray-400 dark:border-gray-500'
                          }`}>
                        <div className="flex items-center gap-1.5">
                          <span className={`font-medium ${isUser ? 'text-white/90' : 'text-gray-700 dark:text-gray-300'
                            }`}>Replying to {msg.replyTo.user}:</span>
                        </div>
                        {msg.replyTo.fileUrl ? (
                          <div className="flex items-center gap-1.5 mt-1">
                            <FaFile className={`w-3 h-3 ${isUser ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
                              }`} />
                            <span className={`truncate ${isUser ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                              }`}>
                              {truncateFileName(msg.replyTo.originalName || 'File')}
                            </span>
                          </div>
                        ) : (
                          <p className={`mt-1 truncate ${isUser ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                            {msg.replyTo.message?.substring(0, 50)}{msg.replyTo.message?.length > 50 ? '...' : ''}
                          </p>
                        )}
                      </div>
                    )}

                    {showText && (
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                    )}

                    {files.length > 0 && (
                      <div className={`flex flex-col gap-2 ${showText ? 'mt-2' : ''}`}>
                        {files.map((file, fIdx) => (
                          <ChatFileBubble
                            key={`${msg._id}-file-${fIdx}`}
                            file={file}
                            isOwn={isUser}
                            apiBaseUrl={API_BASE}
                          />
                        ))}
                      </div>
                    )}

                    {/* Timestamp + Read Receipts */}
                    <div className={`flex items-center gap-1 mt-1 text-xs ${isUser ? 'text-white/60 justify-end' : 'text-gray-400 dark:text-gray-500'}`}>
                      {msg.isEdited && <span className="italic">edited</span>}
                      <span>{msg.pending ? 'Sending...' : msg.failed ? 'Failed to send' : formatTime(msg.timestamp)}</span>
                      {isUser && getTickIcon(msg)}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {/* Admin Typing Indicator */}
        {adminTyping && (
          <div className="flex justify-start">
            <div className="px-4 py-2 rounded-2xl bg-white dark:bg-dark-800 shadow-sm">
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                {assignedEmployee?.name ? `${assignedEmployee.name} is typing...` : 'Support is typing...'}
              </p>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}

        {isAdminUploading && (
          <div className="flex justify-start pb-2">
            <div className="px-4 py-2 rounded-2xl bg-white dark:bg-dark-800 shadow-sm border border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-2">
                <div className="relative w-4 h-4">
                  <svg className="w-full h-full animate-spin" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-gray-200 dark:stroke-dark-700" strokeWidth="4" />
                    <circle cx="18" cy="18" r="16" fill="none" style={{ stroke: userBubbleColor }} strokeWidth="4" strokeDasharray="30 100" />
                  </svg>
                </div>
                <span className="text-xs text-primary-600 dark:text-primary-400 font-medium italic">
                  {assignedEmployee?.name ? `${assignedEmployee.name} is sending files...` : 'Support is sending files...'}
                </span>
              </div>
            </div>
          </div>
        )}

        {error && <div className="sticky bottom-0 p-2 bg-red-500 text-white text-xs rounded-lg text-center z-[100] shadow-lg mb-2">{error}</div>}

        {/* Sending Progress Indicator */}
        {isSending && (
          <div className="flex flex-col items-center justify-center p-3 bg-white/90 dark:bg-dark-800/90 backdrop-blur-sm rounded-2xl border border-gray-100 dark:border-dark-700 shadow-md mx-8 my-4">
            <div className="relative w-14 h-14">
              <svg className="w-full h-full" viewBox="0 0 36 36">
                <circle
                  cx="18"
                  cy="18"
                  r="16"
                  fill="none"
                  className="stroke-gray-100 dark:stroke-dark-700"
                  strokeWidth="3.5"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="16"
                  fill="none"
                  className="transition-all duration-300 ease-in-out"
                  style={{ stroke: userBubbleColor }}
                  strokeWidth="3.5"
                  strokeDasharray="100"
                  strokeDashoffset={100 - (sendProgress || 0)}
                  strokeLinecap="round"
                  transform="rotate(-90 18 18)"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ color: userBubbleColor }}>
                {Math.round(sendProgress || 0)}%
              </div>
            </div>
            <span className="text-xs font-semibold text-gray-500 mt-2 uppercase tracking-widest">Sending Files...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Footer */}
      <div className="user-chatbox-footer border-t border-gray-100 dark:border-dark-700 bg-white dark:bg-dark-800">
        {replyingTo && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-dark-700 border-b border-gray-100 dark:border-dark-600">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">Replying to {replyingTo.user}</p>
              {replyingTo.fileUrl ? (
                <div className="flex items-center gap-2">
                  <FaFile className="w-3 h-3 text-gray-500" />
                  <p className="text-sm text-dark-800 dark:text-white truncate">{truncateFileName(replyingTo.originalName || 'File')}</p>
                </div>
              ) : (
                <p className="text-sm text-dark-800 dark:text-white truncate">{replyingTo.message?.substring(0, 50)}{replyingTo.message?.length > 50 ? '...' : ''}</p>
              )}
            </div>
            <button
              onClick={cancelReply}
              className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
            >
              <FaTimes className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-100 dark:border-dark-700 max-h-[140px] overflow-y-auto bg-gray-50/50 dark:bg-dark-900/50">
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 shadow-sm">
                  <FaFile className="w-3.5 h-3.5 text-primary-500" />
                  <span className="text-xs text-dark-800 dark:text-white truncate max-w-[120px]">{truncateFileName(file.name)}</span>
                  <button
                    onClick={() => removeSelectedFile(index)}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                  >
                    <FaTimes className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="user-chatbox-input-container flex items-end gap-2 p-3">
          <label
            className="p-2.5 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 cursor-pointer transition-colors"
            style={{ color: accentColor }}
          >
            <FaPaperclip className="w-5 h-5" />
            <input
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="hidden"
              multiple
            />
          </label>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleTextareaChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            onKeyDown={(e) => {
              if (isMobile && e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2.5 rounded-2xl bg-gray-100 dark:bg-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 resize-none"
            rows="1"
            style={{ maxHeight: '120px' }}
          />
          <button
            onClick={handleMessageSend}
            disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)}
            className="p-2.5 rounded-full text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{
              backgroundColor: userBubbleColor,
              boxShadow: `0 10px 20px ${userBubbleColor}33`
            }}
          >
            <IoMdSend className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Resize Handle (Desktop Only) */}
      {!isMobile && (
        <div
          onMouseDown={handleMouseDown}
          className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize"
          style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.1) 50%)' }}
        />
      )}

      {/* Chat History Modal */}
      <ChatHistory
        userId={user._id}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
    </div>
  );
};

export default UserChatbox;